import type { IEmployeeRepository } from '../../domain/repositories/IEmployeeRepository';
import type { Employee } from '../../domain/entities/Employee';
import { LocalEmployeeRepository } from './LocalEmployeeRepository';
import type { EmployeeDTO, EmployeeListResponseDTO } from '../dto/EmployeeDTO';
import { toEmployee } from '../mappers/employeeMapper';
import { httpClient } from '@/shared/api/httpClient';
import type { ApiError } from '@/shared/api/ApiError';
import { LocalFaceEmbeddingRepository } from '@/modules/attendance/infrastructure/repositories/LocalFaceEmbeddingRepository';

export class CompositeEmployeeRepository implements IEmployeeRepository {
  private readonly local = new LocalEmployeeRepository();
  private readonly localFaceEmbedding = new LocalFaceEmbeddingRepository();

  async listByAccessRole(accessRoleId: number): Promise<Employee[]> {
    try {
      const res = await httpClient.get<EmployeeListResponseDTO>(
        `/v1/clock/app/${accessRoleId}/employees`,
      );
      const dtos = res.data.data.results;
      // upsertAll and seed run concurrently — both awaited before returning employees
      // so that faceEmbeddings are ready by the time the scan screen starts matching.
      await Promise.all([
        this.local.upsertAll(accessRoleId, dtos),
        this._seedFaceEmbeddings(accessRoleId, dtos),
      ]);

      return dtos.filter((d) => d.status === 1).map(toEmployee);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.kind === 'network' || apiErr.kind === 'timeout') {
        return this.local.listByAccessRole(accessRoleId);
      }
      const cached = await this.local.listByAccessRole(accessRoleId);
      if (cached.length > 0) return cached;
      throw err;
    }
  }

  async findByEntity(idEntity: number): Promise<Employee | null> {
    return this.local.findByEntity(idEntity);
  }

  /** Fire-and-forget helper: seeds faceEmbeddings from remote DTOs. */
  private async _seedFaceEmbeddings(accessRoleId: number, dtos: EmployeeDTO[]): Promise<void> {
    try {
      const candidates = dtos
        .filter(
          (d) =>
            Array.isArray(d.person_embed_v2) &&
            d.person_embed_v2.length === 512 &&
            d.person_embed_v2.every(Number.isFinite),
        )
        .map((d) => ({ idPerson: d.id_person, vector: d.person_embed_v2! }));

      if (candidates.length === 0) return;

      await this.localFaceEmbedding.bulkSeedFromRemote(accessRoleId, candidates);
    } catch (err) {
      console.warn('[CompositeEmployeeRepository] faceEmbedding seed failed (non-fatal):', err);
    }
  }
}
